import { BaseAppDialog } from 'client/modules/app/dialogs/BaseAppDialog';
import { useDialog } from 'client/modules/app/dialogs/hooks/useDialog';
import { NadoDepositMethod, NadoDepositMethodTabs } from 'client/modules/collateral/nadoDeposit/components/NadoDepositMethodTabs';
import { NadoEWalletCashInPanel } from 'client/modules/collateral/nadoDeposit/components/NadoEWalletCashInPanel';
import { NadoDepositAmountStep } from 'client/modules/collateral/nadoDeposit/components/NadoDepositAmountStep';
import { NadoBridgeStatusModal } from 'client/modules/collateral/nadoDeposit/components/NadoBridgeStatusModal';
import { NadoStablecoinReceivePanel } from 'client/modules/collateral/nadoDeposit/components/NadoStablecoinReceivePanel';
import {
  ETHEREUM_DEPOSIT_CHAIN,
} from 'client/modules/collateral/nadoDeposit/config/chains';
import {
  FundingTokenSymbol,
  getFundingSourceChainsForToken,
  isFundingTokenSymbol,
} from 'client/modules/collateral/nadoDeposit/config/fundingRoutes';
import { useNadoDepositForm } from 'client/modules/collateral/nadoDeposit/hooks/useNadoDepositForm';
import { NadoDepositStep } from 'client/modules/collateral/nadoDeposit/types';
import {
  createReceiveSession,
  ReceiveSession,
  restartReceiveSession,
} from 'client/modules/collateral/nadoDeposit/utils/receiveSession';
import { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';

export interface NadoDepositDialogParams {
  defaultMethod?: NadoDepositMethod;
  defaultTokenSymbol?: FundingTokenSymbol;
  defaultSourceChainId?: number;
}

/**
 * Main entry point for the Nado-style deposit dialog.
 *
 * Token-first model: token is selected first, then the chain is filtered to
 * those supporting that token. Chain state follows token state.
 *
 * Defaults: Stablecoin cash-in, USDT token, Ethereum chain.
 */
export function NadoDepositDialog({
  defaultMethod = 'stablecoin',
  defaultTokenSymbol = 'USDT',
  defaultSourceChainId = ETHEREUM_DEPOSIT_CHAIN.chainId,
}: NadoDepositDialogParams = {}) {
  const { hide } = useDialog();
  const { address } = useAccount();
  const initialTokenSymbol = isFundingTokenSymbol(defaultTokenSymbol) ? defaultTokenSymbol : 'USDT';
  const initialChains = getFundingSourceChainsForToken(initialTokenSymbol);
  const initialChainId = initialChains.some((chain) => chain.chainId === defaultSourceChainId)
    ? defaultSourceChainId
    : initialChains[0]?.chainId ?? ETHEREUM_DEPOSIT_CHAIN.chainId;

  const [step, setStep] = useState<NadoDepositStep>('selection');
  const [activeMethod, setActiveMethod] = useState<NadoDepositMethod>(defaultMethod);
  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState<string>(initialTokenSymbol);
  const [selectedChainId, setSelectedChainId] = useState<number>(initialChainId);
  const [receiveSession, setReceiveSession] = useState<ReceiveSession | undefined>();

  const { formData, formActions, bridgeProgress } = useNadoDepositForm({
    selectedChainId,
    selectedTokenSymbol,
    sourceBalanceAddress: receiveSession?.receiveAddress,
  });
  const { onRefreshSourceBalance } = formActions;

  useEffect(() => {
    if (activeMethod !== 'stablecoin' || !address || receiveSession) {
      return;
    }
    setReceiveSession(createReceiveSession(address));
  }, [activeMethod, address, receiveSession]);

  useEffect(() => {
    if (activeMethod !== 'stablecoin' || step !== 'selection') {
      return;
    }

    onRefreshSourceBalance();
    const intervalId = window.setInterval(onRefreshSourceBalance, 3_000);
    return () => window.clearInterval(intervalId);
  }, [activeMethod, onRefreshSourceBalance, step]);

  function handleTokenSelected(tokenSymbol: string) {
    const nextTokenSymbol = isFundingTokenSymbol(tokenSymbol) ? tokenSymbol : 'USDT';
    setSelectedTokenSymbol(nextTokenSymbol);
    setSelectedChainId(ETHEREUM_DEPOSIT_CHAIN.chainId);
    formActions.onAmountChange('');
  }

  function handleContinue() {
    setStep('amount');
  }

  function handleMethodChange(method: NadoDepositMethod) {
    setActiveMethod(method);
    setStep('selection');
  }

  function handleUseAvailableBalance() {
    formActions.onAmountChange(formData.availableBalance);
  }

  function handleRestartReceiveSession() {
    if (!address) {
      setReceiveSession(undefined);
      return;
    }
    setReceiveSession((current) => restartReceiveSession(current, address));
    formActions.onAmountChange('');
  }

  function handleBack() {
    setStep('selection');
  }

  // Prevent closing while a transaction is in-flight
  const isTransacting = [
    'approving', 'quoting', 'bridging', 'bridge_submitted', 'bridge_pending', 'depositing',
    'switching_chain',
  ].includes(formData.buttonState) || formData.isFundingAutomationActive;

  const handleClose = isTransacting ? undefined : hide;

  // Show bridge status modal when bridge is in-flight
  const showBridgeModal =
    bridgeProgress &&
    bridgeProgress.status !== 'delivered' &&
    (formData.buttonState === 'bridge_pending' || formData.buttonState === 'bridge_submitted');

  return (
    <>
      <BaseAppDialog.Container onClose={handleClose}>
        <BaseAppDialog.Title onClose={handleClose}>
          {step === 'amount' && !isTransacting && (
            <button
              onClick={handleBack}
              className="text-text-tertiary hover:text-text-secondary mr-1 transition-colors"
              aria-label="Go back"
            >
              ←
            </button>
          )}
          Deposit
        </BaseAppDialog.Title>
        <BaseAppDialog.Body>
          {step === 'selection' ? (
            <div className="flex flex-col gap-y-4">
              <NadoDepositMethodTabs
                activeMethod={activeMethod}
                onMethodChange={handleMethodChange}
              />
              {activeMethod === 'ewallet' ? (
                <NadoEWalletCashInPanel />
              ) : (
                <NadoStablecoinReceivePanel
                  selectedTokenSymbol={selectedTokenSymbol as FundingTokenSymbol}
                  receiveAddress={receiveSession?.receiveAddress}
                  connectedAddress={address}
                  availableBalance={formData.availableBalance}
                  amount={formData.amount}
                  inputError={formData.inputError}
                  isContinueDisabled={
                    formData.buttonState === 'idle' ||
                    formData.buttonState === 'route_unavailable' ||
                    formData.buttonState === 'quoting'
                  }
                  onTokenSelected={handleTokenSelected}
                  onAmountChange={formActions.onAmountChange}
                  onUseAvailableBalance={handleUseAvailableBalance}
                  onContinue={handleContinue}
                  onRefreshBalance={onRefreshSourceBalance}
                  onRestartReceiveSession={handleRestartReceiveSession}
                />
              )}
            </div>
          ) : (
            <NadoDepositAmountStep
              {...formData}
              {...formActions}
              onBack={handleBack}
              bridgeProgress={bridgeProgress}
            />
          )}
        </BaseAppDialog.Body>
      </BaseAppDialog.Container>

      {/* Rabby-style bridge status modal overlay */}
      {showBridgeModal && bridgeProgress && (
        <NadoBridgeStatusModal
          data={bridgeProgress}
          amount={formData.amount}
          destinationTokenSymbol={selectedTokenSymbol}
        />
      )}
    </>
  );
}
